import { CaracteristicaLesaoEnum, ParteBucalEnum } from 'graphql/types.generated'

export interface TecidoMoleParteBucalModel {
  title: string
}

export interface TecidoMoleCaracteristicaLesaoModel {
  title: string
}

export interface TipoLesaoFundamentalModel {
  title: string
  lesaoEnvolvidaTitle: string
}

export enum TipoLesaoFundamental {
  PLANA = 'PLANA',
  LIQUIDA = 'LIQUIDA',
  TECIDUAL = 'TECIDUAL',
}

export function getTecidosMolesPartesBucais() {
  return (Object.keys(tecidosMolesPartesBucais) as Array<ParteBucalEnum>).sort((a, b) =>
    getTecidosMolesParteBucal(a).title.localeCompare(getTecidosMolesParteBucal(b).title)
  )
}

export function getTecidosMolesParteBucal(value: ParteBucalEnum) {
  return tecidosMolesPartesBucais[value]
}

export function getTipoLesaoFundamental(tipo: TipoLesaoFundamental) {
  return tiposLesoesEnvolvidas[tipo]
}

export function getTecidosMolesCaracteristicaLesao(value: CaracteristicaLesaoEnum) {
  return tecidosMolesCaracteristicasLesao[value]
}

export function getTecidosMolesSuperficies() {
  return [
    CaracteristicaLesaoEnum.SUPERFICIE_LISA,
    CaracteristicaLesaoEnum.SUPERFICIE_RUGOSA,
    CaracteristicaLesaoEnum.SUPERFICIE_ULCERADA,
    CaracteristicaLesaoEnum.SUPERFICIE_GLANULOMATOSA,
    CaracteristicaLesaoEnum.SUPERFICIE_NECROSADA,
  ].sort((a, b) =>
    getTecidosMolesCaracteristicaLesao(a).title.localeCompare(getTecidosMolesCaracteristicaLesao(b).title)
  )
}

const tiposLesoesEnvolvidas: Record<TipoLesaoFundamental, TipoLesaoFundamentalModel> = {
  [TipoLesaoFundamental.PLANA]: {
    title: 'Plana ou elevada',
    lesaoEnvolvidaTitle: 'Lesão plana ou elevada',
  },
  [TipoLesaoFundamental.LIQUIDA]: {
    title: 'Conteúdo líquido',
    lesaoEnvolvidaTitle: 'Lesão com conteúdo líquido',
  },
  [TipoLesaoFundamental.TECIDUAL]: {
    title: 'Envolve perda tecidual',
    lesaoEnvolvidaTitle: 'Lesão envolvendo perda tecidual',
  },
}

const tecidosMolesPartesBucais: Partial<Record<ParteBucalEnum, TecidoMoleParteBucalModel>> = {
  [ParteBucalEnum.AMIDALA]: {
    title: 'Tonsila palatina/amígdala',
  },
  [ParteBucalEnum.BOCA]: {
    title: 'Boca',
  },
  [ParteBucalEnum.ARTICULACAO_TEMPORO_MANDIBULAS]: {
    title: 'Articulação temporomandibular (ATM)',
  },
  [ParteBucalEnum.MAXILA]: {
    title: 'Maxila',
  },
  [ParteBucalEnum.MANDIBULA]: {
    title: 'Mandíbula',
  },
  [ParteBucalEnum.LINGUA]: {
    title: 'Língua',
  },
  [ParteBucalEnum.LABIO_SUPERIOR]: {
    title: 'Lábio superior',
  },
  [ParteBucalEnum.LABIO_INFERIOR]: {
    title: 'Lábio inferior',
  },
  [ParteBucalEnum.PALATO_DURO]: {
    title: 'Palato duro',
  },
  [ParteBucalEnum.PALATO_MOLE]: {
    title: 'Palato mole',
  },
  [ParteBucalEnum.REGIAO_RETROMOLAR]: {
    title: 'Região retromolar',
  },
  [ParteBucalEnum.UVULA]: {
    title: 'Úvula',
  },
  [ParteBucalEnum.MUCOSA_ALVEOLAR]: {
    title: 'Mucosa alveolar',
  },
  [ParteBucalEnum.MUCOSA_JUGAL]: {
    title: 'Mucosa jugal',
  },
  [ParteBucalEnum.GENGIVA]: {
    title: 'Gengiva',
  },
  [ParteBucalEnum.ASSOALHO_BUCAL]: {
    title: 'Assoalho bucal',
  },
  [ParteBucalEnum.COMISSURA_LABIAL]: {
    title: 'Comissura labial',
  },
  [ParteBucalEnum.FRENULO_LINGUAL]: {
    title: 'Frênulo lingual',
  },
  [ParteBucalEnum.FRENULO_LABIAL]: {
    title: 'Frênulo labial',
  },
  [ParteBucalEnum.GLANDULAS_SALIVARES]: {
    title: 'Glândulas salivares',
  },
}

const tecidosMolesCaracteristicasLesao: Record<CaracteristicaLesaoEnum, TecidoMoleCaracteristicaLesaoModel> = {
  [CaracteristicaLesaoEnum.LESAO_PLANA_MACULA]: {
    title: 'Mácula/mancha',
  },
  [CaracteristicaLesaoEnum.LESAO_PLANA_PLACA]: {
    title: 'Placa',
  },
  [CaracteristicaLesaoEnum.LESAO_PLANA_PAPULA]: {
    title: 'Pápula/nódulo',
  },
  [CaracteristicaLesaoEnum.LESAO_PLANA_TUMEFACAO]: {
    title: 'Tumefação/edema',
  },
  [CaracteristicaLesaoEnum.LESAO_PLANA_PSEUDOMEMBRANA]: {
    title: 'Pseudomembrana',
  },
  [CaracteristicaLesaoEnum.LESAO_LIQUIDA_VESICULA]: {
    title: 'Vesícula/bolha',
  },
  [CaracteristicaLesaoEnum.LESAO_LIQUIDA_PUSTULA]: {
    title: 'Pústula',
  },
  [CaracteristicaLesaoEnum.LESAO_TECIDUAL_EROSAO]: {
    title: 'Erosão',
  },
  [CaracteristicaLesaoEnum.LESAO_TECIDUAL_ULCERA]: {
    title: 'Úlcera',
  },
  [CaracteristicaLesaoEnum.LESAO_TECIDUAL_FISSURA]: {
    title: 'Fissura',
  },
  [CaracteristicaLesaoEnum.QUANTIDADE_UNICA]: {
    title: 'Única',
  },
  [CaracteristicaLesaoEnum.QUANTIDADE_BILATERAL]: {
    title: 'Bilateral',
  },
  [CaracteristicaLesaoEnum.QUANTIDADE_MULTIPLAS]: {
    title: 'Múltiplas',
  },
  [CaracteristicaLesaoEnum.BASE_SESSIL]: {
    title: 'Séssil',
  },
  [CaracteristicaLesaoEnum.BASE_PEDICULADA]: {
    title: 'Pediculada',
  },
  [CaracteristicaLesaoEnum.TAMANHO_ATE_4]: {
    title: 'Até 4mm',
  },
  [CaracteristicaLesaoEnum.TAMANHO_ENTRE_5_E_10]: {
    title: 'De 5 à 10mm',
  },
  [CaracteristicaLesaoEnum.TAMANHO_ACIMA_DE_10]: {
    title: 'Mais de 1cm',
  },
  [CaracteristicaLesaoEnum.COR_BRANCA]: {
    title: 'Branca',
  },
  [CaracteristicaLesaoEnum.COR_VIOLACEA]: {
    title: 'Violácea',
  },
  [CaracteristicaLesaoEnum.COR_VERMELHA]: {
    title: 'Eritematosa',
  },
  [CaracteristicaLesaoEnum.COR_ACASTANHADA]: {
    title: 'Acastanhada',
  },
  [CaracteristicaLesaoEnum.SUPERFICIE_LISA]: {
    title: 'Lisa',
  },
  [CaracteristicaLesaoEnum.SUPERFICIE_RUGOSA]: {
    title: 'Rugosa',
  },
  [CaracteristicaLesaoEnum.SUPERFICIE_ULCERADA]: {
    title: 'Ulcerada',
  },
  [CaracteristicaLesaoEnum.SUPERFICIE_GLANULOMATOSA]: {
    title: 'Glanulomatosa',
  },
  [CaracteristicaLesaoEnum.SUPERFICIE_NECROSADA]: {
    title: 'Necrosada',
  },
  [CaracteristicaLesaoEnum.CONSISTENCIA_MOLE]: {
    title: 'Mole',
  },
  [CaracteristicaLesaoEnum.CONSISTENCIA_FIRME]: {
    title: 'Firme',
  },
  [CaracteristicaLesaoEnum.CONSISTENCIA_DURA]: {
    title: 'Dura',
  },
  [CaracteristicaLesaoEnum.SINTOMATOLOGIA_AUSENTE]: {
    title: 'Ausente',
  },
  [CaracteristicaLesaoEnum.SINTOMATOLOGIA_DOR]: {
    title: 'Dor',
  },
  [CaracteristicaLesaoEnum.SINTOMATOLOGIA_PARESTESIA]: {
    title: 'Parestesia',
  },
  [CaracteristicaLesaoEnum.SINTOMATOLOGIA_ARDENCIA]: {
    title: 'Ardência',
  },
}
